import { classes } from "polytype";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { language } from "../../base/i18n/setup.js";
import { config } from "../../config/config.js";
import type { AsyncResourceVariant } from "../../react";
import { provideReact } from "../../react";
import type { SystemSoftwareVersionListQuery } from "../SystemSoftwareVersion";
import { SystemSoftwareVersion } from "../SystemSoftwareVersion";
import type {
  SystemSoftwareData,
  SystemSoftwareListItemData,
  SystemSoftwareListQueryData,
  SystemSoftwareName,
} from "./types.js";
import { SystemSoftwareFullNames } from "./types.js";

export class SystemSoftware extends ReferenceModel {
  public readonly recommendedVersions: SystemSoftwareVersionListQuery;

  public constructor(id: string) {
    super(id);
    this.recommendedVersions = SystemSoftwareVersion.query(this, {
      recommended: true,
    });
  }

  public static ofId(id: string) {
    return new SystemSoftware(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.systemSoftware.find(id);

    if (data) {
      return new SystemSoftwareDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const systemSoftware = await this.find(id);
    assertObjectFound(systemSoftware, "systemSoftware", id);
    return systemSoftware;
  });

  public findDetailed = provideReact(
    () => SystemSoftware.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<SystemSoftwareDetailed | undefined>>;

  public getDetailed = provideReact(
    () => SystemSoftware.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<SystemSoftwareDetailed>>;

  public static query(query: SystemSoftwareListQueryData = {}) {
    return new SystemSoftwareListQuery(query);
  }
}

class SystemSoftwareCommon extends classes(
  DataModel<SystemSoftwareData | SystemSoftwareListItemData>,
  SystemSoftware,
) {
  public readonly name: SystemSoftwareName;
  public readonly fullName: string;
  public readonly tags: string[];
  public readonly translatedDescription: string;

  public constructor(data: SystemSoftwareData | SystemSoftwareListItemData) {
    super([data], [data.id]);
    this.name = data.name as SystemSoftwareName;
    this.fullName =
      SystemSoftwareFullNames[
        this.name as keyof typeof SystemSoftwareFullNames
      ];
    this.tags = data.tags;
    this.translatedDescription =
      language.default.systemSoftware.description[this.name];
  }
}

export class SystemSoftwareDetailed extends classes(
  SystemSoftwareCommon,
  DataModel<SystemSoftwareData>,
) {
  public constructor(data: SystemSoftwareData) {
    super([data], [data]);
  }
}

export class SystemSoftwareListItem extends classes(
  SystemSoftwareCommon,
  DataModel<SystemSoftwareListItemData>,
) {
  public constructor(data: SystemSoftwareListItemData) {
    super([data], [data]);
  }
}

export class SystemSoftwareListQuery extends ListQueryModel<SystemSoftwareListQueryData> {
  public constructor(query: SystemSoftwareListQueryData = {}) {
    super(query);
  }

  public refine(query: SystemSoftwareListQueryData) {
    return new SystemSoftwareListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.systemSoftware.list(this.query);

    return new SystemSoftwareList(
      this.query,
      items.map((d) => new SystemSoftwareListItem(d)),
    );
  }, [this.queryId, "systemSoftware"]);
}

export class SystemSoftwareList extends classes(
  SystemSoftwareListQuery,
  ListDataModel<SystemSoftwareListItem>,
) {
  public constructor(
    query: SystemSoftwareListQueryData,
    systemSoftwares: SystemSoftwareListItem[],
  ) {
    super([query], [systemSoftwares, systemSoftwares.length]);
  }
}
